import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { EMPTY, Observable, of, OperatorFunction, pipe, Subject, UnaryFunction } from 'rxjs';
import { routeNoWebDokusAvailable, routeWebDoku } from '../livedokweb.routes';
import { Webdoku } from '../shared/models/webdoku';
import { AppUrls } from '../route/app-urls';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { catchError, concatMap, defaultIfEmpty, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Status } from '../shared/models/status';
import { WebdokuFactory } from '../shared/helpers/webDoku.factory';
import { WebdokuDto } from '../shared/dto/webdoku-dto';
import { ChangesDto } from '../info/changes-dto';
import { WebDokuNodeValue } from '../shared/models/web-doku-node-value';
import { Changes } from '../info/changes';
import { ChangesFactory } from '../info/changes-factory';
import { ErrorHandlers } from '../shared/helpers/error-handlers';
import { AuthConfig } from 'angular-oauth2-oidc';
import { ActivatedRoute } from '@angular/router';
import { SymbolGroup } from '../shared/models/symbol-group';
import { PlanningJob } from '../shared/models/planning-job';

@Injectable({
  providedIn: 'root',
})
export class WebdokuService {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private authConfig = inject(AuthConfig);

  private selectedWebDoku: Webdoku | undefined;

  private webDokuCache$!: Observable<Webdoku[]>;
  private webDokuLoadError = '';

  constructor() {

    this.route.queryParamMap.pipe(
      map(params => params.get('selectedPlanningJob')),
      filter((selectedPlanningJob): selectedPlanningJob is string => !!selectedPlanningJob),
      tap((selectedPlanningJob) => this.selectPlanningJob(selectedPlanningJob)),
      take(1)).subscribe()

  }

  get webDokuSelectErrorCode() {
    return this.webDokuLoadError.split(':')[0];
  }

  get allWebDokus$(): Observable<Webdoku[]> {
    this.webDokuLoadError = '';
    if (!this.webDokuCache$) {
      this.webDokuCache$ = this.getAllWebDokus().pipe(shareReplay(1));
    }
    return this.webDokuCache$;
  }

  public webDokuChange: Subject<boolean> = new Subject<boolean>();
  public navigate: Subject<string> = new Subject<string>();

  public get isWebDokuSelected(): boolean {
    return this.selectedWebDoku != undefined;
  }

  public get webDokusAvailable$(): Observable<boolean> {
    return this.allWebDokus$.pipe(map((res) => res.length > 0));
  }

  public get moreThanOneWebDokuAvailable$(): Observable<boolean> {
    return this.allWebDokus$.pipe(map((res) => res.length > 1));
  }

  public getWebDokuByName$(name: string): Observable<string> {
    name = name.toLowerCase();
    return this.allWebDokus$.pipe(
      //tap((wd) => console.log(`all`, wd)),
      map((wds) => wds.find((info) => info.text.toLowerCase() === name)),
      //tap((wds) => console.log(`result `, wds)),
      this.filterNullish(),
      switchMap((item) => of(item.id)),
      defaultIfEmpty('')
    );
  }

  public get currentWebDokuId() {
    if (this.selectedWebDoku == undefined) {
      return '';
    }
    return this.selectedWebDoku.id;
  }

  public get currentWebDokuname() {
    if (this.selectedWebDoku == undefined) {
      return '';
    }
    return this.selectedWebDoku.text;
  }

  public get currentWebDokuRedliningAllowed(): boolean {
    if (this.selectedWebDoku == undefined) {
      return false;
    }
    return this.selectedWebDoku.redliningAllowed;
  }

  public get currentWebDokuDeleteDocumentAllowed(): boolean {
    if (this.selectedWebDoku == undefined) {
      return false;
    }
    return this.selectedWebDoku.deleteDocumentAllowed;
  }

  public get currentWebDokuCopyDocumentAllowed(): boolean {
    if (this.selectedWebDoku == undefined) {
      return false;
    }
    return this.selectedWebDoku.copyDocumentAllowed;
  }

  public get currentStatusValues(): Status[] {
    if (this.selectedWebDoku?.status == undefined) {
      return [];
    }
    return this.selectedWebDoku.status;
  }

  public get currentWebDokuHasLoopNodes(): boolean {
    if (this.selectedWebDoku?.hasLoopNodes == undefined) {
      return false;
    }
    return this.selectedWebDoku.hasLoopNodes;
  }

  public get currentWebDokuHasCustomNavigation(): boolean {
    if (this.selectedWebDoku?.hasCustomNavigation == undefined) {
      return false;
    }
    return this.selectedWebDoku.hasCustomNavigation;
  }

  public getStatusById(statusId: string): Status {
    if (this.selectedWebDoku?.status == undefined) {
      return new Status();
    }
    const found = this.selectedWebDoku.status.find((x) => x.id == statusId);
    if (found != null) {
      return found;
    }
    return new Status();
  }

  private getAllWebDokus(): Observable<Webdoku[]> {
    return this.http.get<Webdoku[]>(AppUrls.getApiUrl(AppUrls.urlWebDoku, 'getall')).pipe(
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }

  public getDetails(): Observable<WebDokuNodeValue[]> {
    return this.http
      .get<WebDokuNodeValue[]>(
        AppUrls.getApiUrl(AppUrls.urlWebDoku, 'getdetails') + '?webDokuId=' + this.currentWebDokuId
      )
      .pipe(
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public getChanges(): Observable<Changes[]> {
    return this.http
      .get<ChangesDto[]>(AppUrls.getApiUrl(AppUrls.urlWebDoku, 'getchanges') + '?webDokuId=' + this.currentWebDokuId)
      .pipe(
        map((dtoRaw) => ChangesFactory.fromList(this, dtoRaw)),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public getSymbols(): Observable<SymbolGroup[]> {
    return this.http
      .get<SymbolGroup[]>(AppUrls.getApiUrl(AppUrls.urlWebDoku, 'getsymbols') + '?webDokuId=' + this.currentWebDokuId)
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        catchError((error) => {
          if (error.status === HttpStatusCode.NoContent) {
            return EMPTY;
          }
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  filterNullish<T>(): UnaryFunction<Observable<T | null | undefined>, Observable<T>> {
    return pipe(filter((x) => x != null) as OperatorFunction<T | null | undefined, T>);
  }

  public selectWebDoku(id: string): Observable<boolean> {
    this.webDokuLoadError = '';

    if (this.selectedWebDoku != undefined && this.selectedWebDoku.id == id) {
      return of(true);
    }
    return this.allWebDokus$.pipe(
      // tap((wd) => console.log(`try to find wd ${wd}`)),
      map((wds) => wds.find((info) => info.id === id)),
      this.filterNullish(),
      switchMap((item) => this.selectWebDokuItem(item)),
      tap(() => this.resetPlanningJob()),
      defaultIfEmpty(false)
    );
  }

  private selectWebDokuItem(webDoku: Webdoku): Observable<boolean> {
    this.webDokuLoadError = '';
    return this.http.get<WebdokuDto>(AppUrls.getApiUrl(AppUrls.urlWebDoku, 'get') + '?webDokuId=' + webDoku.id).pipe(
      map((dtoRaw) => WebdokuFactory.fromDTO(dtoRaw)),
      map((res) => {
        this.selectedWebDoku = res;
        this.webDokuChange.next(true);
        return true;
      }),
      catchError((error) => {
        console.error(`select webdoku failed`, error, webDoku.id);
        this.webDokuLoadError = error.error.message;
        return of(false);
        // return ErrorHandlers.errorHandler(error);
      })
    );
  }

  retrieveUserState(state: string) {
    let userState = '';
    if (state) {
      const sep = this.authConfig.nonceStateSeparator || ';';
      const idx = state.indexOf(sep);
      if (idx > -1) {
        userState = state.substr(idx + sep.length);
      }
    }
    return userState;
  }

  public initializeWebDokus() {
    const snapshot = this.route.snapshot;
    const state = snapshot.root.queryParams['state'];
    let userState = '';
    if (state) {
      userState = this.retrieveUserState(decodeURIComponent(state));
    }

    // "load" all records
    this.getAllWebDokus()
      .pipe(
        concatMap(() => this.initOnSuccess(userState)),
        take(1)
      )
      .subscribe();
  }

  // prüfen, ob state webdoku enthält und verfügbar ist
  // ween nicht, dann "Keine webdoku verfügbar"
  //
  private initOnSuccess(userState: string): Observable<boolean> {
    return this.webDokusAvailable$.pipe(
      map((webDokuAvailable) => {
        if (!webDokuAvailable) {
          this.navigate.next(`/${routeNoWebDokusAvailable}`);
          return true;
        } else {
          if (userState) {
            this.navigate.next(userState);
            return true;
          }
        }
        return false;
      }),
      filter((res) => !res),
      switchMap(() => this.moreThanOneWebDokuAvailable$),
      map(
        (moreThanOneWebDokuAvailable) => {
          if (moreThanOneWebDokuAvailable) {
            this.navigate.next(`/${routeWebDoku}`);
            return true;
          }
          return false;
        },
        filter((res) => !res)
        // todo MN
        //switchMap(() => this.selectWebDokuItem()) m(())
      )
    );
  }

  public selectedPlanningJob = signal<PlanningJob | undefined>(undefined)

  public get currentPlanningJobs(): PlanningJob[] {
    if (this.selectedWebDoku?.planningJobs == undefined) {
      return [];
    }
    return this.selectedWebDoku.planningJobs;
  }

  public get currentPlanningJob(): PlanningJob | undefined {
    if (this.selectedWebDoku?.planningJobs == undefined || this.selectedWebDoku.planningJobs.length === 0) {
      return undefined;
    }
    return this.selectedPlanningJob();
  }

  public selectPlanningJob(planningJobName: string) {
    if (this.selectedWebDoku?.planningJobs == undefined) {
      this.selectedPlanningJob?.set(undefined);
      return;
    }

    const found = this.selectedWebDoku?.planningJobs.find((x) => x.name === planningJobName) || undefined;

    this.selectedPlanningJob?.set(found);
    sessionStorage.setItem('selectedPlanningJob', this.selectedPlanningJob ? this.selectedPlanningJob()?.name || '' : '')
    console.log("Planning job selected: ", this.selectedPlanningJob());
    return;

    // this.resetPlanningJob();
  }


  private resetPlanningJob() {
    const storedPlanningJob = sessionStorage.getItem('selectedPlanningJob') || '';
    this.selectPlanningJob(storedPlanningJob)
  }

}
